import { QueryKey } from 'react-query'
import type { QuoteResponse } from '@/components/forms/results/QuoteResults'

export type QuoteNumberSource = {
  quoteNumber?: string | null
  quoteId?: string | null
}

/** Prefer the human-readable 6-digit quoteNumber; fall back to quoteId. */
export const getQuoteNumber = (
  quote?: QuoteNumberSource | null
): string | undefined => quote?.quoteNumber || quote?.quoteId || undefined

export const getQuoteQueryKey = (quoteId: string): QueryKey => [
  'quote',
  quoteId,
]

export class QuoteNotFoundError extends Error {
  constructor(quoteId: string) {
    super(`Quote ${quoteId} was not found`)
    this.name = 'QuoteNotFoundError'
  }
}

const fetchQuoteOnce = async (quoteId: string): Promise<QuoteResponse> => {
  const res = await fetch(
    `/api/internal-api/quotes/${encodeURIComponent(quoteId)}`
  )
  if (res.status === 404) throw new QuoteNotFoundError(quoteId)
  if (!res.ok) throw new Error(`Failed to fetch quote ${quoteId}`)

  const data: QuoteResponse = await res.json()
  if (!data.quote) throw new QuoteNotFoundError(quoteId)

  return data
}

// Quotes are created via an external API and replicated to our internal
// database, so on first navigation the record may not yet be available.
// Retry once after a short delay before surfacing an error.
const RETRY_DELAY_MS = 2000

export const fetchQuote = async (quoteId: string): Promise<QuoteResponse> => {
  try {
    return await fetchQuoteOnce(quoteId)
  } catch {
    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS))
    return fetchQuoteOnce(quoteId)
  }
}
